import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertOctagon, Clock, Users, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { useLoading } from '../hooks/useLoading';
import { sleep } from '../utils/sleep';

const Incidents = () => {
  const navigate = useNavigate();
  const { isLoading, withLoading } = useLoading();

  const handleCreateIncident = async () => {
    await withLoading(async () => {
      // Simulate API call
      await sleep(3000);
      console.log('Creating incident...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Creating incident..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <AlertOctagon className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Incident Response</h1>
            <p className="text-sm text-gray-400">Manage and respond to security incidents</p>
          </div>
        </div>
        <Button onClick={handleCreateIncident}>Create Incident</Button>
      </div>

      {/* Active Incidents */}
      <div className="grid grid-cols-4 gap-6">
        {['Critical', 'High', 'Medium', 'Low'].map((severity) => (
          <div key={severity} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h3 className="text-lg font-semibold text-white mb-2">{severity}</h3>
            <div className="text-3xl font-bold text-white mb-2">3</div>
            <p className="text-sm text-gray-400">Active incidents</p>
          </div>
        ))}
      </div>

      {/* Incident Timeline */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Active Incidents</h2>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 text-xs font-medium bg-red-500/10 text-red-500 rounded-full">
                      Critical
                    </span>
                    <span className="text-sm font-medium text-white">
                      SQL Injection Attack Detected
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Incident ID: INC-2024-001
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate(`/incidents/INC-2024-00${i}`)}
                >
                  View Details
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-xs text-gray-400">Time Detected</div>
                    <div className="text-sm text-white">2 hours ago</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-xs text-gray-400">Assigned To</div>
                    <div className="text-sm text-white">Security Team</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-xs text-gray-400">Updates</div>
                    <div className="text-sm text-white">5 comments</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Response Playbooks */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Response Playbooks</h2>
        <div className="grid grid-cols-3 gap-6">
          {['Data Breach', 'Malware Infection', 'DDoS Attack'].map((type) => (
            <div key={type} className="p-4 bg-gray-800/30 rounded-lg">
              <h3 className="text-sm font-medium text-white mb-2">{type}</h3>
              <p className="text-xs text-gray-400 mb-4">
                Step-by-step response procedures for {type.toLowerCase()} incidents.
              </p>
              <Button variant="outline" size="sm" className="w-full">
                View Playbook
                <span className="ml-2 inline-flex items-center justify-center rounded-full px-1 py-1 text-xs font-medium text-white bg-orange-800/50">
                  <span className="text-[0.625rem] leading-none">Soon!</span>
                </span>
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Incidents;